import { useCallback } from 'react';
import { supabase, Database } from './supabase';
import { getCurrentToken } from '../utils/auth';

export type GameMetrics = Database['public']['Tables']['game_metrics']['Row'];

export const useGameMetrics = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;

  /**
   * Gets metrics for a specific game
   * @param gameId - The ID of the game to get metrics for
   * @returns The GameMetrics object or null if not found
   */
  const getGameMetrics = useCallback(async (gameId: string): Promise<GameMetrics | null> => {
    try {
      const { data: metrics, error } = await supabase
        .from('game_metrics')
        .select('*')
        .eq('game_id', gameId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Record not found
          return null;
        }
        console.error('Error retrieving game metrics:', error);
        throw error;
      }

      return metrics;
    } catch (error) {
      console.error('Exception getting game metrics:', error);
      throw error;
    }
  }, []);

  /**
   * Updates metrics for a specific game
   * @param gameId - The ID of the game to update metrics for
   * @param updates - The metrics fields to update
   * @returns The updated GameMetrics object
   */
  const updateGameMetrics = useCallback(async (
    gameId: string,
    updates: Partial<GameMetrics>
  ): Promise<GameMetrics> => {
    try {
      // First check if metrics exist for this game
      const { data: existingMetrics } = await supabase
        .from('game_metrics')
        .select('id')
        .eq('game_id', gameId)
        .single();

      if (existingMetrics) {
        // Update existing metrics
        const { data: metrics, error } = await supabase
          .from('game_metrics')
          .update(updates)
          .eq('game_id', gameId)
          .select('*')
          .single();

        if (error) {
          console.error('Error updating game metrics:', error);
          throw error;
        }

        return metrics;
      } else {
        // Create new metrics record
        const { data: metrics, error } = await supabase
          .from('game_metrics')
          .insert({
            game_id: gameId,
            ...updates
          })
          .select('*')
          .single();

        if (error) {
          console.error('Error creating game metrics:', error);
          throw error;
        }

        return metrics;
      }
    } catch (error) {
      console.error('Exception updating game metrics:', error);
      throw error;
    }
  }, []);

  /**
   * Increments the play count for a game
   * @param gameId - The ID of the game
   * @returns The updated GameMetrics object
   */
  const incrementPlays = useCallback(async (gameId: string): Promise<GameMetrics | null> => {
    try {
      // console.log('incrementPlays called with gameId:', gameId);
      
      const authToken = await getCurrentToken();
      if (!authToken) {
        // console.log('Skipping incrementPlays: User not logged in.');
        return null;
      }

      const requestBody = {
        gameId,
        metricType: 'plays'
      };
      // console.log('Request payload:', requestBody);

      const functionUrl = `${supabaseUrl}/functions/v1/increment-metrics`;
      // console.log('Function URL:', functionUrl);
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      // console.log('Response status:', response.status);
      // console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error incrementing plays:', errorData);
        throw new Error(`Failed to increment plays: ${errorData.error || response.statusText}`);
      }

      const responseData = await response.json();
      // console.log('Successful response data:', responseData);
      return responseData;
    } catch (error) {
      console.error('Exception incrementing plays:', error);
      console.error('Error details:', error.message, error.stack);
      throw error;
    }
  }, [supabaseUrl]);

  /**
   * Increments the like count for a game
   * @param gameId - The ID of the game
   * @returns The updated GameMetrics object
   */
  const incrementLikes = useCallback(async (gameId: string): Promise<GameMetrics | null> => {
    try {
      // console.log('incrementLikes called with gameId:', gameId);

      const authToken = await getCurrentToken();
      if (!authToken) {
        console.log('Skipping incrementLikes: User not logged in.');
        return null;
      }
      
      const requestBody = {
        gameId,
        metricType: 'likes'
      };
      // console.log('Request payload:', requestBody);
      
      const functionUrl = `${supabaseUrl}/functions/v1/increment-metrics`;
      // console.log('Function URL:', functionUrl);
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      // console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error incrementing likes:', errorData);
        throw new Error(`Failed to increment likes: ${errorData.error || response.statusText}`);
      }

      const responseData = await response.json();
      // console.log('Successful response data:', responseData);
      return responseData;
    } catch (error) {
      console.error('Exception incrementing likes:', error);
      console.error('Error details:', error.message, error.stack);
      throw error;
    }
  }, [supabaseUrl]);

  /**
   * Increments the remix count for a game
   * @param gameId - The ID of the game
   * @returns The updated GameMetrics object
   */
  const incrementRemixes = useCallback(async (gameId: string): Promise<GameMetrics | null> => {
    try {
      // console.log('incrementRemixes called with gameId:', gameId);

      const authToken = await getCurrentToken();
      if (!authToken) {
        console.log('Skipping incrementRemixes: User not logged in.');
        return null;
      }
      
      const requestBody = {
        gameId,
        metricType: 'remixes'
      };
      // console.log('Request payload:', requestBody);
      
      const functionUrl = `${supabaseUrl}/functions/v1/increment-metrics`;
      // console.log('Function URL:', functionUrl);
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      // console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error incrementing remixes:', errorData);
        throw new Error(`Failed to increment remixes: ${errorData.error || response.statusText}`);
      }

      const responseData = await response.json();
      // console.log('Successful response data:', responseData);
      return responseData;
    } catch (error) {
      console.error('Exception incrementing remixes:', error);
      console.error('Error details:', error.message, error.stack);
      throw error;
    }
  }, [supabaseUrl]);

  return {
    getGameMetrics,
    updateGameMetrics,
    incrementPlays,
    incrementLikes,
    incrementRemixes,
  };
};